/**
 * MCP helpers — small, dependency-free utilities used by the MCP picker
 * surfaces. Kept separate from `mcpApi.ts` so the picker can compose its
 * own "synthetic" cards (the BYO Add card, existing-connection cards)
 * without bloating the API client module.
 *
 * Mirrors patterns from LogicAppsUX `libs/designer-v2/.../mcp/helpers.ts`.
 */

import type { McpConnection } from '@/types/logicApps';
import type { ConnectionReference } from '@/types/connectionReference';
import {
  MCP_CLIENT_CONNECTOR_ID,
  type McpServerOperation,
  type McpConnectionInfo,
} from '@/services/mcpApi';

/** Synthetic ID for the "Add MCP server" card in the picker. */
export const ADD_MCP_SERVER_OPERATION_ID = 'addMcpServer';

/**
 * Synthetic `McpServerOperation` representing the "Add MCP server"
 * card that the picker pins to the top of the All and Others tabs. The
 * picker recognises this by `id === ADD_MCP_SERVER_OPERATION_ID` and
 * routes to the inline add-server form instead of the normal selection
 * handler.
 *
 * It is given an empty `api.id` on purpose so the lazy-tools query
 * (`useMcpToolsQuery`) is never enabled for this card.
 */
export const builtinMcpServerOperation: McpServerOperation = {
  id: ADD_MCP_SERVER_OPERATION_ID,
  name: 'Add MCP server',
  type: MCP_CLIENT_CONNECTOR_ID,
  properties: {
    description: 'Connect to a custom Model Context Protocol server',
    api: {
      id: '',
      displayName: 'Add MCP server',
    },
  },
};

/**
 * BYO MCP server card derived from a persisted `agentMcpConnections` record.
 *
 * The picker renders one of these per entry in
 * `connections.json.agentMcpConnections`, then lazily lists its tools via
 * `useMcpToolsForConnection` using the connection's `mcpServerUrl`.
 *
 * `id` is the `agentMcpConnections` record key — the same value that becomes
 * the runtime `McpClientTool`'s `inputs.connectionReference.connectionName`
 * when the tool is added to the agent.
 */
export function mcpConnectionToServer(
  record: McpConnection,
  key: string,
): McpServerOperation {
  const displayName = record.displayName?.trim() || key;
  return {
    id: key,
    name: displayName,
    type: MCP_CLIENT_CONNECTOR_ID,
    properties: {
      // Surface the endpoint as the description so the picker shows it
      // under the server name without needing a separate field.
      description: record.mcpServerUrl,
      api: {
        id: key,
        displayName,
      },
    },
  };
}

/**
 * Pick the `McpConnectionInfo` shape `listMcpTools` expects from a stored
 * BYO `McpConnection` record. Only `mcpServerUrl` + optional
 * `authentication` are populated — the managed-runtime shape is never
 * used for BYO connections.
 *
 * `id` is set to the record key so React Query caches per-connection.
 */
export function mcpConnectionToInfo(
  record: McpConnection,
  key: string,
): { id: string; mcpServerUrl: string; authentication?: Record<string, unknown> } {
  const info: {
    id: string;
    mcpServerUrl: string;
    authentication?: Record<string, unknown>;
  } = {
    id: key,
    mcpServerUrl: record.mcpServerUrl,
  };
  if (record.authentication) {
    info.authentication = record.authentication;
  }
  return info;
}

/**
 * True when the operation is the synthetic "Add MCP server" card.
 */
export function isAddMcpServerCard(server: McpServerOperation): boolean {
  return server.id === ADD_MCP_SERVER_OPERATION_ID;
}

/**
 * Default authentication for a managed MCP connection when the created
 * reference carries none. Matches the connectionStore export-time backfill
 * so list-tools and runtime resolution agree on the auth type.
 */
const DEFAULT_MANAGED_AUTH: Record<string, unknown> = {
  type: 'ManagedServiceIdentity',
};

/**
 * Map a managed-API `ConnectionReference` (the rich shape built by
 * `ConnectionCreationPanel.onComplete`) into the `McpConnectionInfo` shape
 * that `listMcpTools` expects for the managed variant.
 *
 * Returns `undefined` when the reference has no `connectionRuntimeUrl` —
 * without it the managed list-tools body would be incomplete, so we keep
 * the query disabled rather than firing a guaranteed-fail request.
 *
 * `id` is set to the reference key so React Query caches per-connection.
 * When the reference has no `authentication`, we default to
 * `ManagedServiceIdentity` (the connectionStore export backfill).
 */
export function managedReferenceToMcpConnectionInfo(
  reference: ConnectionReference | undefined,
  refKey: string,
): McpConnectionInfo | undefined {
  const connectionRuntimeUrl = reference?.connectionRuntimeUrl;
  if (!connectionRuntimeUrl) {
    return undefined;
  }

  const info: McpConnectionInfo = {
    id: refKey,
    connectionRuntimeUrl,
    authentication: reference?.authentication
      ? { ...reference.authentication }
      : { ...DEFAULT_MANAGED_AUTH },
  };

  if (reference?.connectionProperties) {
    info.connectionProperties = reference.connectionProperties;
  }

  return info;
}
